import { Component, Input } from '@angular/core';
import { calculateExpiryFromAssessmentYear, calculateTimeRemaining, formatExpirationDate, formatTimeRemaining, getComplianceStatus } from '../utils/helpers';
import { CommonService } from 'src/app/core/services/common.service';

export interface IncomeTaxRecord extends Record<string, unknown> {
  areaLocality: string;
  ayDateFromRegistered: string;
  country: string;
  createdAt: string;
  dateOfRegistration: string;
  dateOfSurrender: string | null;
  flatDoorBuilding: string;
  nameOfPremisesBuildingVillage: string;
  pan: string;
  pinCodeZipCode: string;
  roadStreetPostOffice: string;
  searchState: string;
  sectionNumber: string;
  state: string;
  townCityDistrict: string;
  uniqueRegistrationNumber: string;
}

interface ExpirationDetail {
  expirationDate: string;
  timeRemaining: string;
  status: {
    className: string;
  };
}

@Component({
  selector: 'app-income-tax-compliance-card',
  templateUrl: './income-tax-compliance-card.component.html',
  styleUrls: ['./income-tax-compliance-card.component.scss']
})
export class IncomeTaxComplianceCardComponent {
  @Input() incometaxData: IncomeTaxRecord[] = [];
  @Input() syncDate?: string;

  constructor(private commonService: CommonService) { }

  get hasRecords(): boolean {
    return this.incometaxData.length > 0;
  }

  get firstRecord(): IncomeTaxRecord | null {
    return this.incometaxData[0] || null;
  }

  get section12ARecord(): IncomeTaxRecord | undefined {
    return this.incometaxData.find(
      record => record.sectionNumber?.includes('12A') && !record.dateOfSurrender
    );
  }

  get section80GRecord(): IncomeTaxRecord | undefined {
    return this.incometaxData.find(
      record => record.sectionNumber?.includes('80G') && !record.dateOfSurrender
    );
  }

  get formattedSyncDate(): string {
    if (!this.syncDate) {
      return '';
    }
    return this.formatDate(this.syncDate);
  }

  get expirationDetail12A(): ExpirationDetail | null {
    const ayFor12A = this.section12ARecord?.ayDateFromRegistered;
    const ayExpiry12A = ayFor12A ? calculateExpiryFromAssessmentYear(ayFor12A) : null;
    const finalExpiry12A = ayExpiry12A ||
      (this.section12ARecord?.dateOfRegistration
        ? new Date(this.section12ARecord.dateOfRegistration)
        : null);

    if (!finalExpiry12A) {
      return null;
    }

    const timeRemaining = calculateTimeRemaining(finalExpiry12A);
    return {
      expirationDate: formatExpirationDate(finalExpiry12A),
      timeRemaining: formatTimeRemaining(timeRemaining.months, timeRemaining.days),
      status: getComplianceStatus(timeRemaining)
    };
  }

  get expirationDetail80G(): ExpirationDetail | null {
    const ayFor80G = this.section80GRecord?.ayDateFromRegistered;
    const ayExpiry80G = ayFor80G ? calculateExpiryFromAssessmentYear(ayFor80G) : null;
    const finalExpiry80G = ayExpiry80G ||
      (this.section80GRecord?.dateOfRegistration
        ? new Date(this.section80GRecord.dateOfRegistration)
        : null);

    if (!finalExpiry80G) {
      return null;
    }

    const timeRemaining = calculateTimeRemaining(finalExpiry80G);
    return {
      expirationDate: formatExpirationDate(finalExpiry80G),
      timeRemaining: formatTimeRemaining(timeRemaining.months, timeRemaining.days),
      status: getComplianceStatus(timeRemaining)
    };
  }

  formatDate(date: string): string {
    if (!date) return 'N/A';
    try {
      const dateObj = new Date(date);
      return this.commonService.dateFormatter(dateObj.toISOString());
    } catch {
      return "N/A";
    }
  }
}

